"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "@/components/common/Link";
import { Check, X } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DiyVsProfessionalAnalysis() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const comparisonData = [{
    aspect: "Equipment Quality",
    diy: "Consumer-grade, often ineffective",
    professional: "Commercial-grade, EPA-approved solutions"
  }, {
    aspect: "Local Knowledge",
    diy: "Generic approaches, no Port Charlotte expertise",
    professional: "Understanding of local pests and humidity conditions"
  }, {
    aspect: "Safety",
    diy: "Risk of improper chemical handling",
    professional: "Licensed and insured professionals with training"
  }, {
    aspect: "Results",
    diy: "Temporary, recurring infestations",
    professional: "Long-term solutions with guaranteed results"
  }, {
    aspect: "Time Investment",
    diy: "Ongoing effort required",
    professional: "One-time professional service"
  }, {
    aspect: "Cost",
    diy: "Repeated purchases add up quickly",
    professional: "Upfront cost saves money long-term"
  }];
  return <section id="diyvsprofessionalanalysis" ref={sectionRef} className="w-full bg-background py-16 px-4 md:px-8 lg:px-12">
      <div className="max-w-6xl mx-auto">
        {}
        <div className={`mb-12 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h2 className="font-default text-3xl md:text-4xl font-bold text-foreground mb-4">
            Why DIY Pest Control Doesn't Work in Port Charlotte
          </h2>
          <p className="font-serif text-base md:text-lg text-foreground/90 leading-relaxed">
            Port Charlotte's unique climate—characterized by high humidity, proximity to Charlotte Harbor, and tropical pest species—demands more than off-the-shelf solutions. DIY pest control often fails because it doesn't account for these local environmental factors. Professional expertise identifies pest entry points, treats the root cause, and deploys solutions designed for Florida's pest challenges.
          </p>
        </div>

        {}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {}
          <Card className="bg-card rounded-none border-2 border-foreground/20">
            <CardHeader>
              <CardTitle className="font-default text-2xl text-foreground flex items-center gap-2">
                <X className="size-6 text-destructive" />
                DIY Limitations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 font-serif text-foreground/80">
              <div className="flex gap-2">
                <span className="text-destructive font-bold">•</span>
                <span>Limited effectiveness against stubborn pests like termites and mosquitoes</span>
              </div>
              <div className="flex gap-2">
                <span className="text-destructive font-bold">•</span>
                <span>Misses hidden nests, colonies, and entry points</span>
              </div>
              <div className="flex gap-2">
                <span className="text-destructive font-bold">•</span>
                <span>Chemical safety risks without proper training</span>
              </div>
              <div className="flex gap-2">
                <span className="text-destructive font-bold">•</span>
                <span>Repeated treatments drain time and money</span>
              </div>
              <div className="flex gap-2">
                <span className="text-destructive font-bold">•</span>
                <span>No accountability if pests return quickly</span>
              </div>
            </CardContent>
          </Card>

          {}
          <Card className="bg-card rounded-none border-2 border-primary">
            <CardHeader>
              <CardTitle className="font-default text-2xl text-primary flex items-center gap-2">
                <Check className="size-6 text-primary" />
                Professional Advantages
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 font-serif text-foreground/80">
              <div className="flex gap-2">
                <span className="text-primary font-bold">•</span>
                <span>Targeted treatments for Port Charlotte's specific pest species</span>
              </div>
              <div className="flex gap-2">
                <span className="text-primary font-bold">•</span>
                <span>Comprehensive inspection finds all infestation sources</span>
              </div>
              <div className="flex gap-2">
                <span className="text-primary font-bold">•</span>
                <span>Licensed, insured, FDACS-certified operators</span>
              </div>
              <div className="flex gap-2">
                <span className="text-primary font-bold">•</span>
                <span>Guaranteed results with follow-up support</span>
              </div>
              <div className="flex gap-2">
                <span className="text-primary font-bold">•</span>
                <span>Long-term prevention strategies save time and money</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {}
        <div className={`mb-12 transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h3 className="font-default text-2xl font-bold text-foreground mb-6">
            Detailed Comparison
          </h3>
          <Table className="rounded-none border border-foreground/20">
            <TableHeader className="bg-muted/30">
              <TableRow>
                <TableHead className="font-default font-bold text-foreground rounded-none">
                  Aspect
                </TableHead>
                <TableHead className="font-default font-bold text-foreground rounded-none">
                  DIY Approach
                </TableHead>
                <TableHead className="font-default font-bold text-foreground rounded-none">
                  Professional Service
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparisonData.map((row, i) => <TableRow key={i} data-index={i} className="border-foreground/10">
                  <TableCell className="font-default font-semibold text-foreground">
                    {row.aspect}
                  </TableCell>
                  <TableCell className="font-serif text-foreground/80">
                    {row.diy}
                  </TableCell>
                  <TableCell className="font-serif text-foreground/80">
                    {row.professional}
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </div>

        {}
        <div className={`text-center transition-all duration-1000 delay-400 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="font-serif text-lg text-foreground/90 mb-6 max-w-2xl mx-auto">
            Don't let pests take control of your Port Charlotte home. Trust Peace River Pest Control's proven expertise to eliminate infestations and prevent them from returning.
          </p>
          <Link to="/ContactPage">
            <Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-default text-base px-8">
              Get Your Free Inspection Today
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}